import { getDiscountName, getDiscountPriceInfo } from "@repo/shopify/src/utils/order";
import { transformWon } from "@repo/utils/src/number";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import Button from "@components/atoms/Button";
import CompareAtPrice from "@components/atoms/CompareAtPrice";
import Icon from "@components/atoms/Icon";
import TitleWrap from "@components/atoms/TitleWrap";

import useCart from "@hooks/reducerHooks/useCart";
import useUser from "@hooks/reducerHooks/useUser";

import { IconName } from "@const/enum";

function OrderSummary() {
  const { t } = useTranslation();
  const { cart } = useCart();
  const { customer } = useUser();

  // 직원 / 지인 인지 판단
  const isEmployee = useMemo(() => customer.tags.includes("Employee") || customer.tags.includes("FRIENDS"), [customer]);

  const [showAll, setShowAll] = useState(false);

  return (
    <div>
      <TitleWrap title="Order summary" />
      <div className="mt-2.5 flex flex-col gap-2">
        {cart.lines.edges.slice(0, showAll ? undefined : 1).map(({ node }) => {
          const { price, compareAtPrice, isSale, salePercent, membershipDiscountAmount, codeDiscountAmount } =
            getDiscountPriceInfo(node);

          const hasMatchingDiscountCode = node.attributes
            .map(
              (attr) =>
                attr.key === "discount_code" && // "discount_code" 키만 필터링
                node.discountAllocations.some(
                  (discountAllocation) =>
                    discountAllocation.__typename === "CartCodeDiscountAllocation" &&
                    discountAllocation.code.toLowerCase() === attr.value?.toLowerCase(), // code가 일치하는지 비교
                ),
            )
            .some((value) => value);

          return (
            <div className="flex flex-col gap-1" key={node.id}>
              <div className="flex justify-between gap-5">
                <span className="font-regular text-ellipsis">{node.merchandise.product.title}</span>
                <CompareAtPrice
                  className="font-regular"
                  originalPrice={parseInt(node.cost.compareAtAmountPerQuantity?.amount || "0") * node.quantity}
                  salePrice={parseInt(node.cost.totalAmount.amount || "0")}
                />
              </div>

              <div className="flex justify-between">
                <div>
                  <div className="text-tuneGrey-400 flex w-full items-center gap-1 text-[10px]">
                    <span>
                      Size {node.merchandise.selectedOptions.find((item) => item.name === "Size")?.value || ""}
                    </span>
                    <span> x </span>
                    <span>
                      {node.quantity}
                      {t("account.order.count")}
                    </span>
                  </div>
                </div>

                {/* 할인 레이블 */}
                <div>
                  {/* 직원 할인 */}
                  {isEmployee && node.discountAllocations.length ? (
                    <div className="flex flex-col items-end gap-1">
                      {node.discountAllocations.map((discount, index) => (
                        <div className="flex items-center gap-1" key={index}>
                          <Icon alt="discount_grey" height="12px" name={IconName.Discount_grey} width="12px" />
                          <div className="font-regular text-tuneGrey-400 flex gap-0.5 text-sm">
                            <span className="font-regular text-tuneGrey-400 text-sm">{getDiscountName(discount)}</span>
                            <span>
                              {`(-${transformWon((compareAtPrice - price + parseInt(node.discountAllocations[0].discountedAmount.amount) / node.quantity) * node.quantity)}${t("account.order.currency")})`}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : null}

                  {/* 상품 할인 */}
                  {/*  TODO: 특정 할인태그일 경우는 상품할인 + 할인코드드 할인 같이 노출시켜야 함 -> 로직 정리 필요 */}
                  {/*{isSale && !node.discountAllocations.length ? (*/}
                  {(isSale && !node.discountAllocations.length) ||
                  (isSale &&
                    node.discountAllocations.some(
                      (discount) =>
                        discount.__typename === "CartCodeDiscountAllocation" &&
                        ["ppaekga", "mid20"].some((code) => discount.code.toLowerCase().includes(code)),
                    )) ? (
                    <div className="flex flex-col items-end gap-1">
                      <div className="flex items-center gap-1">
                        <Icon alt="discount_grey" height="12px" name={IconName.Discount_grey} width="12px" />
                        <div className="font-regular text-tuneGrey-400 flex gap-0.5 text-sm">
                          <span>{`${t("account.order.discount_product")} ${salePercent}%`}</span>
                          <span>
                            {`(-${transformWon((compareAtPrice - price) * node.quantity)}${t("account.order.currency")})`}
                          </span>
                        </div>
                      </div>
                    </div>
                  ) : null}

                  {/* 멤버십 할인 */}
                  {/* 중복할인이 적용되는 과정에서 잠깐 `할인코드 할인률`만 노출됨 */}
                  {!isEmployee && node.discountAllocations.length ? (
                    <div className="flex flex-col items-end gap-1">
                      {node.discountAllocations.map((discount, index) => (
                        <div className="flex items-center gap-1" key={index}>
                          <Icon alt="discount_grey" height="12px" name={IconName.Discount_grey} width="12px" />
                          <div className="font-regular text-tuneGrey-400 flex gap-0.5 text-sm">
                            <span>{getDiscountName(discount)}</span>
                            <span>
                              {/* eslint-disable-next-line no-nested-ternary */}
                              {discount.__typename === "CartAutomaticDiscountAllocation"
                                ? // 멤버십 할인 (title이 있는 경우)
                                  `(-${transformWon(membershipDiscountAmount * node.quantity)}${t("account.order.currency")})`
                                : discount.__typename === "CartCodeDiscountAllocation"
                                  ? // 할인 코드 할인 (code가 있는 경우)
                                    `(-${transformWon(codeDiscountAmount)}${t("account.order.currency")})`
                                  : null}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : null}

                  {/* 중복할인인 경우에만 노출 */}
                  {!isEmployee && node.discountAllocations.length && node.attributes.length ? (
                    <>
                      {/* 할인코드 할인 */}
                      {node.attributes.length && !hasMatchingDiscountCode && codeDiscountAmount ? (
                        <div className="flex flex-col items-end gap-1">
                          {node.attributes.map((attr, index) =>
                            attr.key === "discount_code" ? (
                              <div className="flex items-center gap-1" key={index}>
                                <Icon alt="discount_grey" height="12px" name={IconName.Discount_grey} width="12px" />
                                <div className="font-regular text-tuneGrey-400 flex gap-0.5 text-sm">
                                  <span className="uppercase" key={attr.key}>
                                    {attr.value}
                                  </span>
                                  <span>{`(-${transformWon(codeDiscountAmount * node.quantity)}${t("account.order.currency")})`}</span>
                                </div>
                              </div>
                            ) : null,
                          )}
                        </div>
                      ) : null}
                    </>
                  ) : null}
                </div>
              </div>
            </div>
          );
        })}

        {cart.lines.edges.length > 1 && (
          <Button
            className="m-auto underline"
            onClick={() => {
              setShowAll((prev) => !prev);
            }}
            variant="text"
          >
            {showAll ? t("account.order.fold") : `${cart.lines.edges.length}${t("account.order.see_all")}`}
          </Button>
        )}
      </div>
    </div>
  );
}

export default OrderSummary;
